/**
 * The vault's listing, with archived deals held back or shown, and one deal's
 * files as a ZIP (R-15, R-16/R-17 — 29 Oct 2026 run-through).
 *
 * The test-data reset archives every deal. The vault went on listing their
 * folders, and each one then asked the pipeline for a deal it hides, and said
 * "Additional deal details could not be loaded". The server now holds archived
 * deals back unless asked, counts them, and says why each one shown is
 * archived — which is what this reads.
 */
import api from '@/services/api'
import { filenameFromDisposition } from '@/services/dealPackage'
import type { VaultDocument, VaultGroup, VaultQuery } from '@/services/documents'
import { toCamelCase } from '@/utils/transform'

export type VaultFolderDocument = VaultDocument & {
  /** Filed under an archived deal, or — not under one — from an archived intake record. */
  archived?: boolean
}

export type VaultFolder = Omit<VaultGroup, 'documents'> & {
  /** When the deal was archived; null for a live deal and for unfiled documents. */
  archivedAt?: string | null
  /** Why, as the deal's trail records it ("Archived by test-data reset"). */
  archiveReason?: string | null
  documents: VaultFolderDocument[]
}

/** What the vault is holding back while archived deals are not shown. */
export type ArchivedCount = { deals: number; documents: number }

export type VaultListing = { groups: VaultFolder[]; total: number; archived: ArchivedCount }

const NONE: ArchivedCount = { deals: 0, documents: 0 }

export const vaultService = {
  /** The vault filed under deals. Live deals only unless `includeArchived`;
   *  a named `loanId` is listed whatever its state. */
  list: (query: VaultQuery = {}, includeArchived = false): Promise<VaultListing> =>
    api
      .get('/documents', {
        params: {
          q: query.q || undefined,
          broker: query.broker || undefined,
          sponsor: query.sponsor || undefined,
          borrower: query.borrower || undefined,
          category: query.category || undefined,
          loan_id: query.loanId || undefined,
          limit: query.limit,
          group_by: 'deal',
          include_archived: includeArchived || undefined,
        },
      })
      .then((r) => {
        const listing = toCamelCase<Partial<VaultListing>>(r.data)
        return { groups: listing.groups ?? [], total: listing.total ?? 0, archived: listing.archived ?? NONE }
      }),

  /** Every file filed under the deal, as one ZIP. Files the server could not
   *  read are listed inside it in MISSING.txt. */
  downloadAll: (loanId: string, fallbackFilename: string): Promise<{ blob: Blob; filename: string }> =>
    api
      .get(`/documents/deals/${encodeURIComponent(loanId)}/download-all`, { responseType: 'blob' })
      .then((r) => ({
        blob: r.data as Blob,
        filename: filenameFromDisposition(r.headers?.['content-disposition']) ?? fallbackFilename,
      })),
}
